import { useState } from 'react';
import API, { isSignedIn, getSession } from '../api';

export default function Terms({ navigate, initialTab }) {
  const [tab, setTab] = useState(initialTab || 'tos');
  const [reportForm, setReportForm] = useState({ buildId: '', reportType: 'abuse', description: '' });
  const [reportStatus, setReportStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const session = getSession();
  const loggedIn = isSignedIn();

  async function submitReport(e) {
    e.preventDefault();
    if (!reportForm.buildId || !reportForm.description) {
      setReportStatus({ error: 'Build ID and description are required' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/v1/abuse-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.token}` },
        body: JSON.stringify(reportForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setReportStatus({ success: data.message });
      setReportForm({ buildId: '', reportType: 'abuse', description: '' });
    } catch (err) {
      setReportStatus({ error: err.message });
    }
    setSubmitting(false);
  }

  const s = {
    container: { maxWidth: 800, margin: '0 auto' },
    tabs: { display: 'flex', gap: 4, marginBottom: 32 },
    tab: (active) => ({
      padding: '8px 20px', borderRadius: 'var(--radius-xs)', cursor: 'pointer',
      fontSize: 14, fontWeight: active ? 600 : 400,
      background: active ? 'var(--accent-glow)' : 'transparent',
      color: active ? 'var(--text)' : 'var(--muted)',
      border: active ? '1px solid var(--accent)' : '1px solid transparent',
    }),
    h1: { fontSize: 28, fontWeight: 700, marginBottom: 8 },
    h2: { fontSize: 20, fontWeight: 600, marginTop: 32, marginBottom: 12, color: 'var(--text)' },
    h3: { fontSize: 16, fontWeight: 600, marginTop: 24, marginBottom: 8, color: 'var(--text)' },
    p: { fontSize: 14, lineHeight: 1.7, color: 'var(--muted)', marginBottom: 12 },
    ul: { paddingLeft: 20, marginBottom: 16 },
    li: { fontSize: 14, lineHeight: 1.8, color: 'var(--muted)' },
    card: {
      background: 'var(--card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: 24, marginTop: 24,
    },
    input: {
      width: '100%', padding: '10px 14px', borderRadius: 'var(--radius-xs)',
      border: '1px solid var(--border)', background: 'var(--bg)',
      color: 'var(--text)', fontSize: 14, marginBottom: 12, boxSizing: 'border-box',
    },
    select: {
      width: '100%', padding: '10px 14px', borderRadius: 'var(--radius-xs)',
      border: '1px solid var(--border)', background: 'var(--bg)',
      color: 'var(--text)', fontSize: 14, marginBottom: 12, boxSizing: 'border-box',
    },
    textarea: {
      width: '100%', padding: '10px 14px', borderRadius: 'var(--radius-xs)',
      border: '1px solid var(--border)', background: 'var(--bg)',
      color: 'var(--text)', fontSize: 14, minHeight: 120, resize: 'vertical',
      marginBottom: 12, boxSizing: 'border-box', fontFamily: 'inherit',
    },
    btn: {
      padding: '10px 24px', borderRadius: 'var(--radius-xs)', fontSize: 14,
      fontWeight: 600, border: 'none',
      background: 'linear-gradient(135deg, var(--accent), #6558d4)',
      color: '#fff', cursor: 'pointer',
    },
    label: { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--muted)', marginBottom: 4 },
    updated: { fontSize: 12, color: 'var(--muted)', marginBottom: 24 },
    success: { padding: '10px 14px', borderRadius: 'var(--radius-xs)', background: 'var(--green-glow)', color: 'var(--green)', fontSize: 13, marginBottom: 12 },
    error: { padding: '10px 14px', borderRadius: 'var(--radius-xs)', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: 13, marginBottom: 12 },
  };

  return (
    <div style={s.container}>
      <div style={{ ...s.tabs, flexWrap: 'wrap' }}>
        <span style={s.tab(tab === 'tos')} onClick={() => setTab('tos')}>Terms of Service</span>
        <span style={s.tab(tab === 'privacy')} onClick={() => setTab('privacy')}>Privacy Policy</span>
        <span style={s.tab(tab === 'refund')} onClick={() => setTab('refund')}>Refund Policy</span>
        <span style={s.tab(tab === 'cookies')} onClick={() => setTab('cookies')}>Cookie Policy</span>
        <span style={s.tab(tab === 'content')} onClick={() => setTab('content')}>Content Policy</span>
        <span style={s.tab(tab === 'dsa')} onClick={() => setTab('dsa')}>DSA / Transparency</span>
        <span style={s.tab(tab === 'report')} onClick={() => setTab('report')}>Report Abuse</span>
      </div>

      {tab === 'tos' && (
        <div>
          <h1 style={s.h1}>Terms of Service</h1>
          <p style={s.updated}>Last updated: April 9, 2026</p>

          <h2 style={s.h2}>1. Overview</h2>
          <p style={s.p}>
            invokeAPI ("<strong style={{ color: 'var(--text)' }}>Platform</strong>") is a SaaS platform for AI and data API services,
            operated by <strong style={{ color: 'var(--text)' }}>invokeAPI EOOD</strong>, a company registered in the
            Republic of Bulgaria (registration pending). The Platform provides access to AI generation services
            (images, video, speech, text) and data tools through a unified API interface.
          </p>
          <p style={s.p}>
            invokeAPI is a <strong style={{ color: 'var(--text)' }}>direct provider</strong> of all services available through the Platform.
            Services are delivered via integration with third-party AI providers (fal.ai, Groq, Perplexity, and others),
            with invokeAPI responsible for service delivery to end users. By using the Platform, you agree to these Terms of Service
            in their entirety. If you do not agree, you must not use the Platform.
          </p>

          <h2 style={s.h2}>2. Accounts &amp; Authentication</h2>
          <p style={s.p}>
            You must provide a valid email address and password to create an account. You are responsible for
            maintaining the security of your credentials and API keys. Sharing or exposing API keys
            may result in unauthorized charges to your account, for which you bear sole responsibility.
          </p>
          <ul style={s.ul}>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>One account per person/entity.</strong> Creating multiple accounts to circumvent limits, bans, or billing controls is prohibited.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Age requirement.</strong> You must be at least 18 years of age (or the age of majority in your jurisdiction, whichever is higher) to use the Platform.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Non-transferable.</strong> Accounts are personal to you and may not be sold, transferred, or assigned to another person or entity.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Identity verification.</strong> We may require identity verification at any time, including when unusual activity is detected or to comply with legal obligations.</li>
          </ul>

          <h2 style={s.h2}>3. Credits &amp; Billing</h2>
          <ul style={s.ul}>
            <li style={s.li}>Credits are <strong style={{ color: 'var(--text)' }}>prepaid service units</strong> that entitle you to make API calls through the Platform. Credits are <strong style={{ color: 'var(--text)' }}>not</strong> stored monetary value, electronic money, a financial instrument, or a currency of any kind.</li>
            <li style={s.li}>Credits are denominated in USD <strong style={{ color: 'var(--text)' }}>for accounting convenience only</strong>. This denomination does not create any right to redeem credits for cash or convert them into any currency.</li>
            <li style={s.li}>Credits are purchased via Stripe checkout and added to your account balance immediately upon successful payment.</li>
            <li style={s.li}>Each API call deducts credits based on the service pricing or the specific tool used.</li>
            <li style={s.li}>Failed calls (errors, timeouts) are automatically refunded to your credit balance.</li>
            <li style={s.li}>Consumers may dispute a charge within 72 hours of the call.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>EU right of withdrawal:</strong> By purchasing credits, you consent to immediate service activation and <strong style={{ color: 'var(--text)' }}>acknowledge that you waive your EU right of withdrawal</strong> (under Article 16(a) of the Consumer Rights Directive 2011/83/EU) for any credits that have been used to make API calls. For unused credits, see the Refund Policy.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Unused credits:</strong> Refundable within 14 days of your first purchase, provided no API calls have been made. After 14 days, unused credits are non-refundable except as described in the Refund Policy.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Credit expiration:</strong> Credits expire 12 months after your last account activity (defined as any API call, credit purchase, or sign-in). You will receive a reminder email 30 days before expiration.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Cost confirmation:</strong> By default, the Platform provides a cost preview before executing API calls. You may opt out of cost previews in your account settings, in which case you accept full responsibility for all charges incurred.</li>
          </ul>

          <h2 style={s.h2}>4. Services &amp; Limitations</h2>
          <p style={s.p}>
            invokeAPI provides AI and data services through integration with third-party APIs. All services available through the Platform
            are provided by invokeAPI, and we take responsibility for their availability and quality within the limitations described below.
          </p>
          <ul style={s.ul}>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Availability:</strong> We make reasonable efforts to ensure uninterrupted access to services, but do not guarantee 100% uptime. Services depend on third-party AI providers which may have their own outages.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>AI outputs:</strong> Results from AI generation (images, video, text, speech) are produced by third-party AI models. invokeAPI does not control and cannot guarantee the accuracy, quality, or fitness for purpose of generated results.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Service changes:</strong> We reserve the right to add, modify, or remove services at any time. For material changes, we will notify users at least 7 days in advance.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Prohibited use:</strong> Using the Platform to generate illegal content, infringe on third-party rights, send spam, attempt to bypass restrictions, or other activities contrary to applicable law is prohibited.</li>
          </ul>

          <h2 style={s.h2}>5. Intellectual Property</h2>
          <ul style={s.ul}>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>The Platform:</strong> All code, design, trademarks, and other intellectual property of invokeAPI remain the property of invokeAPI EOOD.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Generated outputs:</strong> Rights to AI-generated results (images, video, text, etc.) are governed by the terms of use of the respective AI provider and applicable law. invokeAPI does not claim ownership over user-generated results.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>User inputs:</strong> Data you provide through API requests (prompts, text, images) remains your property. invokeAPI does not store API request/response content except for temporary technical purposes (caching, binary file delivery).</li>
          </ul>

          <h2 style={s.h2}>6. Rate Limits</h2>
          <p style={s.p}>
            To ensure fair usage: 60 requests/minute per IP globally, 30 calls/minute per consumer
            per service, and configurable daily spending limits. Exceeding limits returns HTTP 429.
          </p>

          <h2 style={s.h2}>7. Disputes</h2>
          <p style={s.p}>
            Consumers may file a dispute within 72 hours of a call. Disputes are reviewed by
            Platform administrators within 48 hours. Outcomes: refund (credits returned to your balance) or denial (with written explanation).
          </p>

          <h2 style={s.h2}>8. Termination</h2>
          <p style={s.p}>
            We may suspend or terminate accounts that violate these terms, engage in fraudulent activity,
            or abuse the Platform. Remaining credit balances may be forfeited in cases of confirmed fraud.
            You may close your account at any time by contacting <strong style={{ color: 'var(--text)' }}>support@invoke-api.com</strong>.
            Upon account closure, unused credits are handled in accordance with the Refund Policy.
          </p>

          <h2 style={s.h2}>9. Limitation of Liability</h2>
          <p style={s.p}>
            <strong style={{ color: 'var(--text)' }}>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:</strong>
          </p>
          <ul style={s.ul}>
            <li style={s.li}>invokeAPI's <strong style={{ color: 'var(--text)' }}>total aggregate liability</strong> to you for any and all claims arising out of or relating to these Terms or your use of the Platform shall not exceed the lesser of: (a) the total amount of credits purchased by you in the 12 months preceding the event giving rise to the claim, or (b) EUR 100.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>IN NO EVENT</strong> shall invokeAPI, its officers, directors, employees, or agents be liable for: indirect, incidental, special, consequential, or punitive damages of any kind; loss of profits, revenue, data, goodwill, or business opportunity; damages arising from unauthorized access to your account due to your failure to secure credentials.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>AI outputs:</strong> invokeAPI is not responsible for the accuracy, completeness, legality, or fitness for purpose of AI-generated results. AI service outputs do <strong style={{ color: 'var(--text)' }}>not</strong> constitute financial, medical, legal, or any other professional advice.</li>
            <li style={s.li}>The Platform is provided on an "<strong style={{ color: 'var(--text)' }}>AS IS</strong>" and "<strong style={{ color: 'var(--text)' }}>AS AVAILABLE</strong>" basis, without warranties of any kind, whether express, implied, or statutory, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</li>
          </ul>
          <p style={s.p}>
            Some jurisdictions do not allow the exclusion or limitation of liability for certain types of damages. In such jurisdictions, our liability is limited to the maximum extent permitted by applicable law. Nothing in these Terms excludes or limits liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded or limited under applicable law.
          </p>

          <h2 style={s.h2}>10. Indemnification</h2>
          <p style={s.p}>
            You agree to indemnify, defend, and hold harmless invokeAPI EOOD, its officers, directors, employees, contractors, and agents from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable legal fees) arising from or related to:
          </p>
          <ul style={s.ul}>
            <li style={s.li}>Your use of the Platform or any service accessed through it;</li>
            <li style={s.li}>Your violation of these Terms of Service or any applicable law;</li>
            <li style={s.li}>Content you generate through the Platform and how you use it;</li>
            <li style={s.li}>Your violation of any third-party rights.</li>
          </ul>

          <h2 style={s.h2}>11. Governing Law &amp; Dispute Resolution</h2>
          <ul style={s.ul}>
            <li style={s.li}>These Terms are governed by and construed in accordance with the <strong style={{ color: 'var(--text)' }}>laws of the Republic of Bulgaria</strong>, without regard to conflict of law principles.</li>
            <li style={s.li}>Any disputes arising out of or relating to these Terms or your use of the Platform shall be resolved by the <strong style={{ color: 'var(--text)' }}>competent courts of Sofia, Bulgaria</strong>, unless mandatory consumer protection law in your jurisdiction provides otherwise.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Mandatory pre-litigation resolution:</strong> Before filing any claim or lawsuit, you must first attempt to resolve the dispute informally by contacting <strong style={{ color: 'var(--text)' }}>support@invoke-api.com</strong>. We will attempt to resolve the dispute within 30 days.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Class action waiver:</strong> To the maximum extent permitted by applicable law, you agree to resolve disputes with invokeAPI on an individual basis only, and not as part of any class, collective, or representative action or proceeding.</li>
            <li style={s.li}>EU consumers may also use the European Commission's <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>Online Dispute Resolution platform</a>.</li>
          </ul>

          <h2 style={s.h2}>12. Changes to These Terms</h2>
          <p style={s.p}>
            We may update these Terms from time to time. For <strong style={{ color: 'var(--text)' }}>material changes</strong>, we will notify you via email at the address associated with your account at least <strong style={{ color: 'var(--text)' }}>14 days before</strong> the changes take effect. If you disagree with the updated Terms, you may close your account and request a refund of unused credits in accordance with the Refund Policy before the effective date. Continued use of the Platform after the effective date of any changes constitutes your acceptance of the updated Terms.
          </p>
        </div>
      )}

      {tab === 'privacy' && (
        <div>
          <h1 style={s.h1}>Privacy Policy</h1>
          <p style={s.updated}>Last updated: April 9, 2026</p>

          <h2 style={s.h2}>1. Information We Collect</h2>
          <h3 style={s.h3}>Account Information</h3>
          <ul style={s.ul}>
            <li style={s.li}>Email address and password (hashed, never stored in plaintext)</li>
            <li style={s.li}>Display name (optional)</li>
            <li style={s.li}>API keys (generated by the Platform)</li>
          </ul>
          <h3 style={s.h3}>Billing Information</h3>
          <ul style={s.ul}>
            <li style={s.li}>Payment details are collected and processed exclusively by <strong style={{ color: 'var(--text)' }}>Stripe</strong>. We never see, store, or have access to your credit card numbers.</li>
            <li style={s.li}>We store Stripe customer IDs, transaction amounts, and session references for accounting purposes.</li>
          </ul>
          <h3 style={s.h3}>Usage Data</h3>
          <ul style={s.ul}>
            <li style={s.li}>API call logs: timestamp, service called, response status code, credits charged, latency</li>
            <li style={s.li}>We do <strong style={{ color: 'var(--text)' }}>not</strong> log or store the content of API request/response bodies</li>
            <li style={s.li}>IP addresses for rate limiting (not permanently stored)</li>
          </ul>

          <h2 style={s.h2}>2. How We Use Your Information</h2>
          <ul style={s.ul}>
            <li style={s.li}>To operate and maintain your account</li>
            <li style={s.li}>To process payments</li>
            <li style={s.li}>To enforce rate limits and prevent fraud</li>
            <li style={s.li}>To send transactional emails (account verification, billing receipts)</li>
            <li style={s.li}>To respond to support requests and abuse reports</li>
          </ul>
          <p style={s.p}>
            We do <strong style={{ color: 'var(--text)' }}>not</strong> sell your data to third parties. We do <strong style={{ color: 'var(--text)' }}>not</strong> use your data for advertising or profiling.
          </p>

          <h2 style={s.h2}>3. Data Sharing</h2>
          <ul style={s.ul}>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Stripe:</strong> Payment processing (governed by <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>Stripe's Privacy Policy</a>)</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>AI Providers:</strong> When you call a service, the content of your request (prompt, text, image) is sent to the relevant AI provider (fal.ai, Groq, Perplexity, etc.) for processing. Your email, API key, and account details are never shared with AI providers.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Law enforcement:</strong> Only when required by valid legal process (subpoena, court order)</li>
          </ul>

          <h2 style={s.h2}>4. Data Retention</h2>
          <ul style={s.ul}>
            <li style={s.li}>Account data: retained while your account is active. Deleted within 30 days of account deletion request.</li>
            <li style={s.li}>Call logs: retained for 90 days for billing disputes, then anonymized.</li>
            <li style={s.li}>Billing records: retained for 7 years as required by tax law.</li>
          </ul>

          <h2 style={s.h2}>5. Your Rights</h2>
          <p style={s.p}>
            We provide self-service tools for data access and deletion. You can exercise these rights directly through our API or by contacting us.
          </p>
          <ul style={s.ul}>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Access &amp; Export:</strong> Request a copy of all data we hold about you via <strong style={{ color: 'var(--text)' }}>GET /v1/me/export-data</strong> (authenticated) or by emailing <strong style={{ color: 'var(--text)' }}>privacy@invoke-api.com</strong></li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Deletion:</strong> Request account and data deletion via <strong style={{ color: 'var(--text)' }}>POST /v1/me/delete-account</strong> (authenticated) or by emailing <strong style={{ color: 'var(--text)' }}>privacy@invoke-api.com</strong></li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Correction:</strong> Update your account information through the dashboard</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Portability:</strong> Export your call history and billing data in machine-readable format via the export endpoint above</li>
          </ul>

          <h2 style={s.h2}>6. Security</h2>
          <p style={s.p}>
            All data is transmitted over HTTPS/TLS. Passwords are hashed using bcrypt. API keys are generated
            with cryptographically secure random bytes. Database access is restricted to the application server only.
          </p>

          <h2 style={s.h2}>7. Cookies</h2>
          <p style={s.p}>
            We use only essential session cookies (JWT token stored in localStorage) for authentication.
            We do not use tracking cookies, analytics pixels, or third-party advertising cookies.
          </p>

          <h2 style={s.h2}>8. Data Controller</h2>
          <p style={s.p}>
            invokeAPI is operated by <strong style={{ color: 'var(--text)' }}>invokeAPI EOOD</strong>, a company registered
            in the Republic of Bulgaria (registration pending). For GDPR purposes, invokeAPI EOOD is the
            data controller for all personal data processed through the Platform.
            Contact: <strong style={{ color: 'var(--text)' }}>privacy@invoke-api.com</strong>.
          </p>

          <h2 style={s.h2}>9. Legal Basis for Processing (GDPR)</h2>
          <ul style={s.ul}>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Contract performance:</strong> Processing necessary to provide the service (account management, API calls, billing)</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Legitimate interest:</strong> Fraud prevention, rate limiting, platform security</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Legal obligation:</strong> Tax records, law enforcement requests</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Consent:</strong> Optional marketing communications (if any — currently none)</li>
          </ul>

          <h2 style={s.h2}>10. International Data Transfers</h2>
          <p style={s.p}>
            Our servers are hosted on Railway (US-based infrastructure). If you are located in the EU/EEA,
            your data may be transferred to the United States. We rely on standard contractual clauses and
            our hosting providers' compliance frameworks to ensure adequate data protection.
          </p>

          <h2 style={s.h2}>11. GDPR Rights (EU/EEA Residents)</h2>
          <p style={s.p}>
            In addition to the rights listed above, EU/EEA residents have the right to:
          </p>
          <ul style={s.ul}>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Restrict processing:</strong> Request that we limit how we use your data</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Object to processing:</strong> Object to processing based on legitimate interest</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Withdraw consent:</strong> Where processing is based on consent, withdraw at any time</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Lodge a complaint:</strong> File a complaint with your local data protection authority</li>
          </ul>
          <p style={s.p}>
            To exercise any right, email <strong style={{ color: 'var(--text)' }}>privacy@invoke-api.com</strong>. We will respond within 30 days.
          </p>

          <h2 style={s.h2}>12. Children</h2>
          <p style={s.p}>
            invokeAPI is not directed at children under 18. We do not knowingly collect information
            from minors. If you believe a minor has created an account, contact us at privacy@invoke-api.com.
          </p>

          <h2 style={s.h2}>13. Changes</h2>
          <p style={s.p}>
            We may update this Privacy Policy from time to time. Material changes will be communicated
            via email at least 14 days before they take effect. Continued use after changes constitutes acceptance.
          </p>

          <h2 style={s.h2}>14. Contact</h2>
          <p style={s.p}>
            For privacy-related inquiries: <strong style={{ color: 'var(--text)' }}>privacy@invoke-api.com</strong>
          </p>
        </div>
      )}

      {tab === 'refund' && (
        <div>
          <h1 style={s.h1}>Refund Policy</h1>
          <p style={s.updated}>Last updated: April 9, 2026</p>

          <h2 style={s.h2}>1. Credit Purchases</h2>
          <p style={s.p}>
            invokeAPI operates on a prepaid credit system. Credits are purchased in USD via Stripe and
            added to your account balance immediately upon successful payment.
          </p>

          <h2 style={s.h2}>2. Automatic Refunds</h2>
          <p style={s.p}>
            The following scenarios result in automatic credit refunds (no action required):
          </p>
          <ul style={s.ul}>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Failed API calls:</strong> If a service returns an error (4xx or 5xx), credits are automatically refunded to your balance.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Timeout errors:</strong> If a call exceeds the maximum timeout, credits are refunded.</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Platform errors:</strong> Any error caused by invokeAPI infrastructure results in a full refund of the call cost.</li>
          </ul>

          <h2 style={s.h2}>3. Dispute-Based Refunds</h2>
          <ul style={s.ul}>
            <li style={s.li}>You may dispute any API call charge within <strong style={{ color: 'var(--text)' }}>72 hours</strong> of the call.</li>
            <li style={s.li}>Disputes are submitted through the Consumer Dashboard.</li>
            <li style={s.li}>Our team reviews disputes within <strong style={{ color: 'var(--text)' }}>48 hours</strong>.</li>
            <li style={s.li}>If approved, credits are returned to your account balance.</li>
            <li style={s.li}>If denied, you will receive an explanation of the decision.</li>
          </ul>

          <h2 style={s.h2}>4. Credit Balance Refunds</h2>
          <p style={s.p}>
            Unused credit balances are generally <strong style={{ color: 'var(--text)' }}>non-refundable</strong> to original payment method. However, we will consider refund requests in the following cases:
          </p>
          <ul style={s.ul}>
            <li style={s.li}>Account closure within 14 days of first credit purchase (cooling-off period for EU consumers)</li>
            <li style={s.li}>Duplicate or accidental purchases (contact us within 48 hours)</li>
            <li style={s.li}>Platform discontinuation or extended outage (more than 72 hours)</li>
          </ul>

          <h2 style={s.h2}>5. How to Request a Refund</h2>
          <ul style={s.ul}>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>API call disputes:</strong> Use the "Dispute" button in your Consumer Dashboard</li>
            <li style={s.li}><strong style={{ color: 'var(--text)' }}>Credit balance refunds:</strong> Email <strong style={{ color: 'var(--text)' }}>support@invoke-api.com</strong> with your account email and purchase details</li>
          </ul>
          <p style={s.p}>
            Refunds to original payment method are processed via Stripe and may take 5-10 business days to appear on your statement.
          </p>

          <h2 style={s.h2}>6. Contact</h2>
          <p style={s.p}>
            For refund inquiries: <strong style={{ color: 'var(--text)' }}>support@invoke-api.com</strong>
          </p>
        </div>
      )}

      {tab === 'cookies' && (
        <div>
          <h1 style={s.h1}>Cookie Policy</h1>
          <p style={s.updated}>Last updated: April 9, 2026</p>

          <h2 style={s.h2}>1. What Are Cookies</h2>
          <p style={s.p}>
            Cookies are small text files stored on your device when you visit a website. They are widely
            used to make websites work efficiently and provide information to site operators.
          </p>

          <h2 style={s.h2}>2. Cookies We Use</h2>
          <p style={s.p}>
            invokeAPI uses <strong style={{ color: 'var(--text)' }}>only strictly necessary cookies</strong>. We do not use any tracking, analytics, or advertising cookies.
          </p>
          <div style={s.card}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text)', fontWeight: 600 }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text)', fontWeight: 600 }}>Purpose</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text)', fontWeight: 600 }}>Duration</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text)', fontWeight: 600 }}>Type</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '8px 12px', color: 'var(--muted)' }}>session (localStorage)</td>
                  <td style={{ padding: '8px 12px', color: 'var(--muted)' }}>JWT authentication token — keeps you signed in</td>
                  <td style={{ padding: '8px 12px', color: 'var(--muted)' }}>Until sign-out</td>
                  <td style={{ padding: '8px 12px', color: 'var(--muted)' }}>Essential</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px 12px', color: 'var(--muted)' }}>cookie_consent (localStorage)</td>
                  <td style={{ padding: '8px 12px', color: 'var(--muted)' }}>Remembers your cookie consent preference</td>
                  <td style={{ padding: '8px 12px', color: 'var(--muted)' }}>1 year</td>
                  <td style={{ padding: '8px 12px', color: 'var(--muted)' }}>Essential</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 style={s.h2}>3. Third-Party Cookies</h2>
          <p style={s.p}>
            <strong style={{ color: 'var(--text)' }}>Stripe:</strong> When you make a payment, Stripe may set cookies on its checkout page to process
            your transaction securely and prevent fraud. These cookies are governed by{' '}
            <a href="https://stripe.com/cookies-policy/legal" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>
              Stripe's Cookie Policy
            </a>.
          </p>

          <h2 style={s.h2}>4. How to Control Cookies</h2>
          <p style={s.p}>
            You can clear localStorage data through your browser's developer tools (Application → Local Storage).
            Note that clearing session data will sign you out. Most browsers also allow you to block cookies
            through their settings, but this may prevent you from signing in to invokeAPI.
          </p>

          <h2 style={s.h2}>5. Changes</h2>
          <p style={s.p}>
            If we introduce new types of cookies (e.g., analytics), we will update this policy and request
            your consent before setting non-essential cookies.
          </p>

          <h2 style={s.h2}>6. Contact</h2>
          <p style={s.p}>
            Questions about cookies? Email <strong style={{ color: 'var(--text)' }}>privacy@invoke-api.com</strong>.
          </p>
        </div>
      )}

      {tab === 'content' && (
        <div>
          <h1 style={s.h1}>Content Policy</h1>
          <p style={s.updated}>Last updated: April 9, 2026</p>

          <p style={s.p}>
            All use of invokeAPI services must comply with this Content Policy. Violations may result
            in account suspension or permanent ban.
          </p>

          <h2 style={s.h2}>Prohibited Content &amp; Behavior</h2>

          <h3 style={s.h3}>Illegal Activity</h3>
          <ul style={s.ul}>
            <li style={s.li}>Using services to facilitate illegal activity in any jurisdiction</li>
            <li style={s.li}>Generating copyrighted material without authorization</li>
            <li style={s.li}>Money laundering or fraudulent financial activities</li>
          </ul>

          <h3 style={s.h3}>Harmful Content</h3>
          <ul style={s.ul}>
            <li style={s.li}>CSAM or exploitation of minors</li>
            <li style={s.li}>Non-consensual intimate imagery or deepfakes intended to deceive</li>
            <li style={s.li}>Content designed for harassment, doxxing, or unauthorized personal data exposure</li>
          </ul>

          <h3 style={s.h3}>Platform Abuse</h3>
          <ul style={s.ul}>
            <li style={s.li}>Attempting to bypass rate limits, billing, or security controls</li>
            <li style={s.li}>Automated abuse or scraping of platform services</li>
            <li style={s.li}>Using the platform to relay traffic to unrelated services</li>
            <li style={s.li}>Creating multiple accounts to circumvent restrictions</li>
          </ul>

          <h2 style={s.h2}>Enforcement</h2>
          <ul style={s.ul}>
            <li style={s.li}><strong>First offense:</strong> Warning + temporary account restriction</li>
            <li style={s.li}><strong>Repeat offense:</strong> Account suspension</li>
            <li style={s.li}><strong>Severe violation:</strong> Immediate permanent ban + law enforcement referral if applicable</li>
          </ul>

          <h2 style={s.h2}>Reporting</h2>
          <p style={s.p}>
            If you encounter a build that violates this policy, please use the{' '}
            <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => setTab('report')}>
              Report Abuse
            </span>{' '}
            form or email us at abuse@invoke-api.com.
          </p>
        </div>
      )}

      {tab === 'dsa' && (
        <div>
          <h1 style={s.h1}>DSA / Transparency</h1>
          <p style={s.updated}>Last updated: April 9, 2026</p>
          <p style={s.p}>
            This page provides transparency information in the spirit of the <strong style={{ color: 'var(--text)' }}>EU Digital Services Act</strong> (Regulation (EU) 2022/2065).
          </p>

          <h2 style={s.h2}>1. Legal Entity</h2>
          <p style={s.p}>
            <strong style={{ color: 'var(--text)' }}>invokeAPI EOOD</strong>, a company registered in the Republic of Bulgaria (registration pending).
          </p>
          <ul style={s.ul}>
            <li style={s.li}>General contact: <strong style={{ color: 'var(--text)' }}>legal@invoke-api.com</strong></li>
            <li style={s.li}>Website: <a href="https://invoke-api.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>https://invoke-api.com</a></li>
          </ul>

          <h2 style={s.h2}>2. Designated Contact Point</h2>
          <p style={s.p}>
            For authorities and users regarding DSA matters, the single point of contact is: <strong style={{ color: 'var(--text)' }}>legal@invoke-api.com</strong>
          </p>
          <p style={s.p}>
            Languages accepted: English, Bulgarian. We aim to respond to all inquiries within 5 business days.
          </p>

          <h2 style={s.h2}>3. Service Classification</h2>
          <p style={s.p}>
            invokeAPI is a <strong style={{ color: 'var(--text)' }}>SaaS provider of AI API services</strong>. The Platform provides access to AI generative models
            and data tools, with all services operated and controlled by invokeAPI EOOD. The Platform does <strong style={{ color: 'var(--text)' }}>not host</strong> third-party
            content and is <strong style={{ color: 'var(--text)' }}>not a marketplace</strong> for external providers.
          </p>

          <h2 style={s.h2}>4. Abuse Reporting</h2>
          <p style={s.p}>
            To report abuse of invokeAPI services:
          </p>
          <ul style={s.ul}>
            <li style={s.li}>Use the{' '}
              <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => setTab('report')}>Report Abuse</span>{' '}
              form on this website, or</li>
            <li style={s.li}>Email <strong style={{ color: 'var(--text)' }}>abuse@invoke-api.com</strong></li>
          </ul>
          <p style={s.p}>
            We <strong style={{ color: 'var(--text)' }}>acknowledge</strong> all reports within <strong style={{ color: 'var(--text)' }}>24 hours</strong> and
            take action within <strong style={{ color: 'var(--text)' }}>48 hours</strong>.
          </p>

          <h2 style={s.h2}>5. Transparency Report</h2>
          <p style={s.p}>
            In accordance with Article 15 of the DSA, invokeAPI publishes an annual transparency report containing:
          </p>
          <ul style={s.ul}>
            <li style={s.li}>Number of abuse reports received</li>
            <li style={s.li}>Number of accounts suspended or banned</li>
            <li style={s.li}>Average response time to reports</li>
            <li style={s.li}>Number of appeals received and their outcomes</li>
            <li style={s.li}>Orders received from authorities of EU Member States</li>
          </ul>
          <div style={s.card}>
            <p style={{ ...s.p, marginBottom: 0, fontStyle: 'italic' }}>
              First transparency report will be published after 12 months of operation. Data collection began April 2026.
            </p>
          </div>

          <h2 style={s.h2}>6. Algorithmic Transparency</h2>
          <ul style={s.ul}>
            <li style={s.li}>invokeAPI does <strong style={{ color: 'var(--text)' }}>not</strong> use algorithmic recommendation systems to promote, rank, or personalize service listings.</li>
            <li style={s.li}>Service listings are <strong style={{ color: 'var(--text)' }}>not</strong> algorithmically ranked or promoted based on user profiles, behavior, or preferences.</li>
            <li style={s.li}>Search results are based on keyword matching and categories only. No profiling or behavioral targeting is used.</li>
          </ul>

          <h2 style={s.h2}>7. Right to Explanation &amp; Appeal</h2>
          <ul style={s.ul}>
            <li style={s.li}>If your account is suspended, you will receive a <strong style={{ color: 'var(--text)' }}>written explanation via email</strong> detailing the reasons for the decision.</li>
            <li style={s.li}>You may <strong style={{ color: 'var(--text)' }}>appeal</strong> any decision by emailing <strong style={{ color: 'var(--text)' }}>legal@invoke-api.com</strong> within <strong style={{ color: 'var(--text)' }}>14 days</strong> of receiving the notification.</li>
            <li style={s.li}>Appeals are reviewed by a person who was not involved in the original decision. You will receive the outcome of your appeal within 14 days.</li>
            <li style={s.li}>You also have the right to refer the matter to an out-of-court dispute settlement body certified under the DSA, or to seek judicial remedies in your Member State.</li>
          </ul>
        </div>
      )}

      {tab === 'report' && (
        <div>
          <h1 style={s.h1}>Report Abuse</h1>
          <p style={s.p}>
            Found something that violates our content policy? Submit a report and we'll investigate within 48 hours.
          </p>

          {!loggedIn ? (
            <div style={s.card}>
              <p style={{ ...s.p, marginBottom: 16 }}>You must be signed in to submit a report.</p>
              <button style={s.btn} onClick={() => navigate('signin')}>Sign In</button>
            </div>
          ) : (
            <div style={s.card}>
              {reportStatus?.success && <div style={s.success}>{reportStatus.success}</div>}
              {reportStatus?.error && <div style={s.error}>{reportStatus.error}</div>}

              <form onSubmit={submitReport}>
                <label style={s.label}>Build ID or Slug</label>
                <input
                  style={s.input}
                  placeholder="e.g. drc-pattern-scanner or build UUID"
                  value={reportForm.buildId}
                  onChange={e => setReportForm({ ...reportForm, buildId: e.target.value })}
                />

                <label style={s.label}>Report Type</label>
                <select
                  style={s.select}
                  value={reportForm.reportType}
                  onChange={e => setReportForm({ ...reportForm, reportType: e.target.value })}
                >
                  <option value="content_violation">Content Violation</option>
                  <option value="fraud">Fraud / Billing Manipulation</option>
                  <option value="abuse">Platform Abuse</option>
                  <option value="illegal_content">Illegal Content (DSA Notice)</option>
                  <option value="other">Other</option>
                </select>

                <label style={s.label}>Description</label>
                <textarea
                  style={s.textarea}
                  placeholder="Describe the issue in detail..."
                  value={reportForm.description}
                  onChange={e => setReportForm({ ...reportForm, description: e.target.value })}
                  maxLength={1000}
                />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {reportForm.description.length}/1000
                  </span>
                  <button style={s.btn} type="submit" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
